import { ErrorResponse, type ErrorCode } from '@deizi/shared';
import type { z } from 'zod';

export const API_BASE = '/api/v1';

export class ApiClientError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode | 'network_error',
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

let csrfToken: string | null = null;
let onUnauthorized: (() => void) | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

/** CSRF header for requests made outside `request` (uploads with progress). */
export function csrfHeaders(): Record<string, string> {
  return csrfToken ? { 'x-csrf-token': csrfToken } : {};
}

/** Called when any request returns 401 (session expired or ended elsewhere). */
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  onUnauthorized = fn;
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

interface RequestOptions {
  body?: unknown;
  idempotencyKey?: string;
  /** Don't trigger the global 401 handler (used by the session probe and login). */
  quiet401?: boolean;
}

/**
 * The single way the web app talks to the API. Responses are validated with the shared Zod
 * schemas, so a contract drift fails loudly in development.
 */
export async function request<S extends z.ZodType>(
  method: Method,
  path: string,
  schema: S,
  opts: RequestOptions = {},
): Promise<z.infer<S>> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (opts.body !== undefined) headers['content-type'] = 'application/json';
  if (method !== 'GET' && csrfToken) headers['x-csrf-token'] = csrfToken;
  if (opts.idempotencyKey) headers['idempotency-key'] = opts.idempotencyKey;

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      credentials: 'same-origin',
      ...(opts.body === undefined ? {} : { body: JSON.stringify(opts.body) }),
    });
  } catch {
    throw new ApiClientError(0, 'network_error', 'Sem conexão com o servidor.');
  }

  const text = await res.text();
  const json: unknown = text ? JSON.parse(text) : undefined;

  if (!res.ok) {
    const parsed = ErrorResponse.safeParse(json);
    const err = parsed.success
      ? new ApiClientError(
          res.status,
          parsed.data.error.code,
          parsed.data.error.message,
          parsed.data.error.details,
        )
      : new ApiClientError(res.status, 'internal_error', `HTTP ${res.status}`);
    if (res.status === 401 && !opts.quiet401) onUnauthorized?.();
    throw err;
  }
  return schema.parse(json);
}

export const api = {
  get: <S extends z.ZodType>(path: string, schema: S, opts?: RequestOptions) =>
    request('GET', path, schema, opts),
  post: <S extends z.ZodType>(path: string, schema: S, body?: unknown, opts?: RequestOptions) =>
    request('POST', path, schema, { ...opts, body }),
  patch: <S extends z.ZodType>(path: string, schema: S, body?: unknown, opts?: RequestOptions) =>
    request('PATCH', path, schema, { ...opts, body }),
  put: <S extends z.ZodType>(path: string, schema: S, body?: unknown, opts?: RequestOptions) =>
    request('PUT', path, schema, { ...opts, body }),
  delete: <S extends z.ZodType>(path: string, schema: S, opts?: RequestOptions) =>
    request('DELETE', path, schema, opts),
};

export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}
